import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ToolRegistry } from '../../core/tools/ToolRegistry';
import { IToolContext, ITool } from '../../core/tools/interfaces/ITool';
import {
  Plus,
  Minus,
  MousePointer2,
  Hand,
  Map,
  Undo2,
  Redo2,
  Type,
  Frame,
  MessageSquare,
  CircleHelp,
  ImagePlus,
  Shapes,
  Waypoints,
  StickyNote,
  PencilLine,
  Highlighter,
  Eraser,
  ChevronUp,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ChromeMountPoint } from '../canvas/chrome/ChromeMountPoint';
import { useUIStore } from '../../stores/uiStore';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { FramesPanel } from '../canvas/FramesPanel';
import { useDiagramStore } from '../../stores/diagramStore';
import { ThemeToggle } from '../ui/ThemeToggle';
import { Logger } from '../../utils/logger';
import { useCrispChat } from '../../lib/crisp';
import { STICKY_NOTE_COLORS } from '../../tools/universal/StickyNoteTool';
import { resolveConnectorToolActivation } from '../canvas/lib/connectorToolActivation';

const logger = Logger.getLoggerWithPrefix('BottomToolbar');

const PEN_TOOL_OPTIONS = [
  { id: 'pen', label: 'Pen', icon: PencilLine, width: 2, opacity: 1 },
  { id: 'highlighter', label: 'Highlighter', icon: Highlighter, width: 12, opacity: 0.4 },
  { id: 'eraser', label: 'Eraser', icon: Eraser, width: 20, opacity: 1 },
];

const PEN_COLORS = ['#000000', '#EF4444', '#3B82F6', '#16A34A'];

// Tool ids that keep the legacy `tool-<id>` data-testid because e2e specs
// in tests/e2e/comments, tests/e2e/dia/editor/undo, and
// tests/e2e/editor/frames-tool look them up without a fallback selector.
// All other tools use `tid.editor.tool.<id>`.
const LEGACY_TESTID_TOOLS: ReadonlySet<string> = new Set(['text', 'frames', 'comments']);

type ToolbarEventBus = {
  on?: (event: string, handler: (arg?: unknown) => void) => () => void;
  emit?: (event: string, payload?: unknown) => void;
};

type ToolbarWindow = Window & {
  __getPenColor?: () => string | undefined;
  __getPenTool?: () => string | undefined;
  __openShapePicker?: () => void;
  __openStickyNote?: () => void;
  __openPenTool?: () => void;
  __openImageUploadModal?: () => void;
  __konvaStage?: {
    width?: () => number;
    height?: () => number;
  };
};

interface BottomToolbarProps {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomTo: (scale: number) => void;
  onZoomReset: () => void;
  onZoomFit: () => void;
  toolRegistry?: ToolRegistry;
  toolContext?: IToolContext;
  onToolExecute?: (toolId: string, params?: unknown) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  hiddenTools?: string[];
}

export const BottomToolbar: React.FC<BottomToolbarProps> = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onZoomTo,
  onZoomReset,
  onZoomFit,
  toolRegistry,
  toolContext,
  onToolExecute,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  hiddenTools = [],
}) => {
  const { t } = useTranslation('diagram');
  const [activeToolId, setActiveToolId] = useState<string | null>(
    toolRegistry?.getActiveTool()?.manifest.id ?? null
  );
  const [minimapOpen, setMinimapOpen] = useState(false);
  const [penColor, setPenColor] = useState('#000000');
  const [penToolMode, setPenToolMode] = useState('pen');
  const [stickyColorId, setStickyColorId] = useState(STICKY_NOTE_COLORS[0]?.id ?? 'yellow');
  const [noteMenuMode, setNoteMenuMode] = useState<'sticky-note' | 'pen'>('sticky-note');
  const [noteMenuOpen, setNoteMenuOpen] = useState(false);
  const isPanMode = useUIStore(s => s.isPanMode);
  const setPanMode = useUIStore(s => s.setPanMode);
  const setActivePanel = useUIStore(s => s.setActivePanel);
  const setPropertyPanelUserClosed = useUIStore(s => s.setPropertyPanelUserClosed);
  const stage = useDiagramStore(s => s.stage);
  const setStage = useDiagramStore(s => s.setStage);
  const { openChat } = useCrispChat();

  // Poll for pen color and tool mode when pen is active
  useEffect(() => {
    if (activeToolId === 'pen') {
      const interval = setInterval(() => {
        try {
          const color = (window as ToolbarWindow).__getPenColor?.();
          if (color) setPenColor(color);
          const toolMode = (window as ToolbarWindow).__getPenTool?.();
          if (toolMode) setPenToolMode(toolMode);
        } catch (e) {
          logger.warn('Failed to poll pen toolbar state', e);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [activeToolId]);

  // Trigger re-computation of tool list when tools register/unregister
  const [toolsVersion, setToolsVersion] = useState(0);
  useEffect(() => {
    if (!toolContext?.eventBus) return;
    const eb = toolContext.eventBus as ToolbarEventBus;
    const inc = () => setToolsVersion((v) => v + 1);
    const offReg = eb.on?.('tool:registered', inc) || (() => {});
    const offUnreg = eb.on?.('tool:unregistered', inc) || (() => {});
    return () => { try { offReg(); offUnreg(); } catch (e) { logger.warn('Failed to unsubscribe toolbar registry listener', e); }
    };
  }, [toolContext?.eventBus]);

  // Listen for tool activation/deactivation to update button highlight
  useEffect(() => {
    if (!toolContext?.eventBus) return;
    const eb = toolContext.eventBus as { on?: (event: string, handler: (arg: unknown) => void) => () => void };
    const offActivated = eb.on?.('tool:activated', (tool: unknown) => {
      try { setActiveToolId((tool as ITool | null)?.manifest?.id ?? null); } catch (e) { logger.warn(e); }
    }) || (() => {});
    const offDeactivated = eb.on?.('tool:deactivated', () => {
      try { setActiveToolId(toolRegistry?.getActiveTool()?.manifest.id ?? null); } catch (e) { logger.warn(e); }
    }) || (() => {});
    return () => {
      try { offActivated(); } catch (e) { logger.warn(e); }
      try { offDeactivated(); } catch (e) { logger.warn(e); }
    };
  }, [toolContext?.eventBus, toolRegistry]);

  // Zoom controls are anchored inside the primary canvas container so split
  // projection panes do not inherit the primary canvas controls.

  // TODO(emergent): migrate to canvas chrome manifests — creation/mode
  // cluster contents should come from ui.canvasCreate / ui.canvasMode via
  // the registry, not these consts (emergent-canvas-chrome plan §12).
  // Define the universal tool order for bottom toolbar (stable)
  const toolOrder = useMemo(
    () => [
      'selection',
      'hand',
      'shape-picker',
      'sticky-note',
      'text',
      'comments',
      'frames',
      'pen',
      'genogram-connector',
      'lasso',
    ],
    []
  );

  // Get tools in the specified order
  const orderedTools = useMemo(() => {
    void toolsVersion;
    if (!toolRegistry || !toolContext) return [] as ITool[];
    const tools: ITool[] = [];
    for (const toolId of toolOrder) {
      const tool = toolRegistry.getTool(toolId) as ITool | undefined;
      if (tool) tools.push(tool);
    }
    return tools;
  }, [toolRegistry, toolContext, toolOrder, toolsVersion]);

  // Handle tool execution
  const handleToolExecute = async (tool: ITool, params?: unknown) => {
    try {
      const id = tool.manifest.id as string;

      // Special handling for tools with popups
      if (id === 'shape-picker') {
        try {
          (window as ToolbarWindow).__openShapePicker?.();
        } catch (e) {
          logger.warn('Failed to open shape picker', e);
        }
        return;
      }
      if (id === 'sticky-note') {
        const colorId = typeof (params as { color?: unknown } | undefined)?.color === 'string'
          ? String((params as { color: string }).color)
          : stickyColorId;
        await activateStickyNoteMode(colorId);
        return;
      }
      if (id === 'pen') {
        await activatePenMode(params as { tool?: string; color?: string; width?: number; opacity?: number } | undefined);
        return;
      }
      if (id === 'genogram-connector') {
        const eventBus = toolContext?.eventBus as ToolbarEventBus | undefined;

        setPanMode(false);
        try {
          eventBus?.emit?.('canvas:set-pan', false);
        } catch (e) {
          logger.warn('Failed to disable canvas pan mode', e);
        }

        try {
          toolRegistry?.activateTool('genogram-connector');
        } catch (e) {
          logger.warn('Failed to activate connector tool', e);
        }

        try {
          const activation = resolveConnectorToolActivation(toolContext?.selection);
          if (activation.event === 'tool:start-connecting') {
            eventBus?.emit?.('tool:start-connecting', activation.payload);
          } else {
            eventBus?.emit?.('tool:arm-connecting');
          }
        } catch (e) {
          logger.warn('Failed to start connector mode', e);
        }
        setActiveToolId('genogram-connector');
        return;
      }

      // Exit pan mode when any tool other than hand is picked
      try {
        if (tool?.manifest?.id && tool.manifest.id !== 'hand') {
          setPanMode(false);
          (toolContext?.eventBus as ToolbarEventBus | undefined)?.emit?.('canvas:set-pan', false);
          setActiveToolId(tool.manifest.id);
        }
      } catch (e) {
        logger.warn('Failed to leave pan mode for toolbar tool', e);
      }
      if (toolRegistry) {
        const isActive = toolRegistry.isToolActive(id);
        if (id === 'comments') {
          setPropertyPanelUserClosed(false);
          setActivePanel('comments');
        }
        if (id === 'pen') {
          const hasParams = params && typeof params === 'object' && Object.keys(params as Record<string, unknown>).length > 0;
          if (!isActive) {
            try { toolRegistry.activateTool('pen'); } catch (e) { logger.warn(e); }
          } else if (!hasParams) {
            // Only toggle off when there are no option params (pure toggle click)
            try { toolRegistry.activateTool('genogram-selection'); } catch (e) { logger.warn(e); }
            return;
          }
        } else {
          try { toolRegistry.activateTool(id); } catch (e) { logger.warn(e); }
        }
      }
      if (onToolExecute) {
        onToolExecute(tool.manifest.id, params);
      } else if (toolRegistry) {
        await toolRegistry.executeTool(tool.manifest.id, params);
      }
    } catch (error) {
      logger.error(`Failed to execute tool ${tool.manifest.id}:`, error);
    }
  };

  const zoomPresets = useMemo(
    () => [
      { label: '200%', value: 2 },
      { label: '150%', value: 1.5 },
      { label: '125%', value: 1.25 },
      { label: '100% (1:1)', value: 1 },
      { label: '75%', value: 0.75 },
      { label: '50%', value: 0.5 },
      { label: '25%', value: 0.25 }
    ],
    []
  );
  const approxEqual = (a: number, b: number) => Math.abs(a - b) < 0.005;

  const executeActiveToolConfig = async (toolId: string, params?: unknown) => {
    const tool = toolRegistry?.getTool(toolId) as ITool | undefined;
    if (!tool) return;
    try {
      await tool.execute(params);
      (toolContext?.eventBus as ToolbarEventBus | undefined)?.emit?.('tool:executed', { tool, params, result: null });
    } catch (e) {
      logger.warn(`Failed to configure ${toolId}`, e);
    }
  };

  const activateStickyNoteMode = async (colorId = stickyColorId, startPlacement = true) => {
    const noteColor = STICKY_NOTE_COLORS.find((color) => color.id === colorId) || STICKY_NOTE_COLORS[0];
    const nextColorId = noteColor?.id ?? 'yellow';
    setNoteMenuMode('sticky-note');
    setStickyColorId(nextColorId);
    setPanMode(false);
    try { (toolContext?.eventBus as ToolbarEventBus | undefined)?.emit?.('canvas:set-pan', false); } catch (e) { logger.warn('Failed to disable canvas pan mode', e); }
    try {
      toolRegistry?.activateTool('sticky-note');
      setActiveToolId('sticky-note');
      if (startPlacement) {
        const executed = await toolRegistry?.executeTool('sticky-note', { color: nextColorId });
        if (!executed) {
          await executeActiveToolConfig('sticky-note', { color: nextColorId });
        }
      }
    } catch (e) {
      logger.warn('Failed to activate sticky note mode', e);
    }
  };

  const activatePenMode = async (params?: { tool?: string; color?: string; width?: number; opacity?: number }) => {
    const nextMode = params?.tool || penToolMode;
    const option = PEN_TOOL_OPTIONS.find((candidate) => candidate.id === nextMode) || PEN_TOOL_OPTIONS[0];
    const nextColor = params?.color || penColor;
    const payload = {
      tool: option.id,
      color: nextColor,
      width: typeof params?.width === 'number' ? params.width : option.width,
      opacity: typeof params?.opacity === 'number' ? params.opacity : option.opacity,
    };

    setNoteMenuMode('pen');
    setPenToolMode(option.id);
    setPenColor(nextColor);
    setPanMode(false);
    try { (toolContext?.eventBus as ToolbarEventBus | undefined)?.emit?.('canvas:set-pan', false); } catch (e) { logger.warn('Failed to disable canvas pan mode', e); }
    try {
      toolRegistry?.activateTool('pen');
      setActiveToolId('pen');
      const executed = await toolRegistry?.executeTool('pen', payload);
      if (!executed) {
        await executeActiveToolConfig('pen', payload);
      }
    } catch (e) {
      logger.warn('Failed to activate pen mode', e);
    }
  };

  const handleMinimapPan = (x: number, y: number) => {
    const stageNode = (window as ToolbarWindow).__konvaStage;
    const stageWidth = typeof stageNode?.width === 'function' ? stageNode.width() : window.innerWidth;
    const stageHeight = typeof stageNode?.height === 'function' ? stageNode.height() : window.innerHeight;
    const viewportWidth = stageWidth / stage.scale;
    const viewportHeight = stageHeight / stage.scale;

    const newOffsetX = -(x - viewportWidth / 2) * stage.scale;
    const newOffsetY = -(y - viewportHeight / 2) * stage.scale;

    setStage({
      ...stage,
      offsetX: newOffsetX,
      offsetY: newOffsetY,
    });
  };

  const getToolIcon = (toolId: string, isActive: boolean) => {
    const iconClass = cn(
      'h-[18px] w-[18px] stroke-[1.9] transition-transform duration-150',
      isActive ? 'opacity-100' : 'opacity-100 group-hover:scale-105'
    );
    const iconProps = { className: iconClass, strokeWidth: 1.9 };

    switch (toolId) {
      case 'shape-picker':
        return <Shapes {...iconProps} />;
      case 'connector':
        return <Waypoints {...iconProps} />;
      case 'sticky-note':
        return <StickyNote {...iconProps} />;
      case 'pen': {
        const PenIcon =
          penToolMode === 'highlighter' ? Highlighter
          : penToolMode === 'eraser' ? Eraser
          : PencilLine;
        // `penColor` only tints the icon when the pen is active so users can
        // tell at a glance which colour they're drawing in.
        return (
          <PenIcon
            className={iconClass}
            strokeWidth={1.9}
            style={isActive ? { color: penColor } : undefined}
          />
        );
      }
      case 'text':
        return <Type {...iconProps} />;
      case 'frames':
        return <Frame {...iconProps} />;
      case 'comments':
        return <MessageSquare {...iconProps} />;
      default:
        return null;
    }
  };

  const navigationToolOrder = useMemo(
    () => ['selection', 'hand'].filter(id => !hiddenTools.includes(id)),
    [hiddenTools]
  );

  // Define toolbar tool order matching the design, filtering out domain-hidden tools
  const toolbarOrder = useMemo(
    () => ['shape-picker', 'connector', 'note-tools', 'text', 'frames', 'comments']
      .filter(id => !hiddenTools.includes(id)),
    [hiddenTools]
  );

  const toolLabel = (id: string, fallback?: string) => {
    switch (id) {
      case 'shape-picker':
        return t('tools.shapes');
      case 'selection':
        return t('tools.select');
      case 'hand':
        return t('tools.pan');
      case 'connector':
        return t('tools.connectors');
      case 'sticky-note':
        return t('tools.stickyNotes');
      case 'note-tools':
        return t('tools.notesAndMedia', { defaultValue: 'Notes and media' }) as string;
      case 'pen':
        return t('tools.pen');
      case 'image-picker':
        return t('tools.insertImage', { defaultValue: 'Insert image' }) as string;
      case 'text':
        return t('tools.text', { defaultValue: 'Text' }) as string;
      case 'frames':
        return t('tools.frames', { defaultValue: 'Frames' }) as string;
      case 'comments':
        return t('tools.comments', { defaultValue: 'Comments' }) as string;
      default:
        return fallback || id;
    }
  };

  const noteMenuItems = [
    { id: 'sticky-note', icon: StickyNote, label: toolLabel('sticky-note') },
    { id: 'pen', icon: PencilLine, label: toolLabel('pen') },
    { id: 'image-picker', icon: ImagePlus, label: toolLabel('image-picker', 'Insert image') },
  ].filter((item) => {
    if (hiddenTools.includes(item.id)) return false;
    if (item.id === 'image-picker') return true;
    return orderedTools.some((tool) => tool.manifest.id === item.id);
  });

  const handleNoteMenuSelect = async (id: string) => {
    if (id === 'image-picker') {
      try {
        (window as ToolbarWindow).__openImageUploadModal?.();
        setNoteMenuOpen(false);
      } catch (e) {
        logger.warn('Failed to open image upload modal', e);
      }
      return;
    }

    const tool = orderedTools.find((candidate) => candidate.manifest.id === id);
    if (tool) {
      await handleToolExecute(tool);
    }
  };

  const selectedStickyColor = STICKY_NOTE_COLORS.find((color) => color.id === stickyColorId) || STICKY_NOTE_COLORS[0];

  const activateSelectionTool = () => {
    try { toolRegistry?.activateTool('genogram-selection'); } catch (e) { logger.warn(e); }
    try { (toolContext?.eventBus as ToolbarEventBus | undefined)?.emit?.('canvas:set-pan', false); } catch (e) { logger.warn('Failed to disable canvas pan mode', e); }
    setPanMode(false);
    setActiveToolId('genogram-selection');
  };

  const togglePanTool = () => {
    const next = !isPanMode;
    setPanMode(next);
    try { (toolContext?.eventBus as ToolbarEventBus | undefined)?.emit?.('canvas:set-pan', next); } catch (e) { logger.warn('Failed to toggle canvas pan mode', e); }
    setActiveToolId(next ? 'hand-pan-mode' : null);
  };

  const railButtonClass = (isActive = false) => cn(
    "group relative h-10 w-10 cursor-pointer rounded-[13px] flex items-center justify-center",
    "transition-[background-color,box-shadow,color,transform] duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3478DD]/40",
    isActive
      ? "bg-[#EEF2F7] text-[#1E293B] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] dark:bg-white/12 dark:text-white"
      : "text-[#334155] hover:-translate-y-px hover:bg-[#EEF2F7] hover:text-[#0F172A] dark:text-slate-100 dark:hover:bg-white/12 dark:hover:text-white"
  );

  const railIconClass = (isActive = false) => cn(
    "h-[18px] w-[18px] stroke-[1.9] transition-transform duration-150",
    isActive ? "opacity-100" : "opacity-100 group-hover:scale-105"
  );

  const railDivider = (
    <div className="mx-0.5 h-8 w-px bg-gradient-to-b from-transparent via-[#CBD5E1] to-transparent dark:via-white/15" />
  );

  return (
    <>

      {/* Main Bottom Toolbar - Centered */}
      <div data-testid="tid.editor.bottomToolbar" className={cn(
        "absolute bottom-6 left-1/2 transform -translate-x-1/2 z-50",
        "h-[54px] rounded-[24px] border border-[#CBD5E1] bg-white px-2 py-1.5",
        "shadow-[0_18px_46px_rgba(15,23,42,0.18),0_3px_10px_rgba(15,23,42,0.1)] backdrop-blur-xl",
        "dark:border-white/20 dark:bg-[#101722]",
        "flex items-center gap-1.5"
      )}>
        <TooltipProvider skipDelayDuration={0} disableHoverableContent>

          {/* Navigation tools */}
          {navigationToolOrder.map((toolId) => {
            const isSelection = toolId === 'selection';
            const isActive = isSelection
              ? activeToolId === 'genogram-selection' && !isPanMode
              : isPanMode;
            const label = toolLabel(toolId);
            const Icon = isSelection ? MousePointer2 : Hand;

            return (
              <Tooltip key={toolId}>
                <TooltipTrigger asChild>
                  <button
                    className={railButtonClass(isActive)}
                    data-testid={`tid.editor.tool.${toolId}`}
                    aria-label={label}
                    aria-pressed={isActive}
                    onClick={isSelection ? activateSelectionTool : togglePanTool}
                  >
                    <Icon className={railIconClass(isActive)} strokeWidth={1.9} />
                  </button>
                </TooltipTrigger>
                <TooltipContent key={label}>{label}</TooltipContent>
              </Tooltip>
            );
          })}

          {navigationToolOrder.length > 0 && toolbarOrder.length > 0 && (
            railDivider
          )}

          {/* Tool buttons */}
          {toolbarOrder.map((toolId) => {
            if (toolId === 'note-tools') {
              if (noteMenuItems.length === 0) return null;
              const isActive = activeToolId === 'pen' || activeToolId === 'sticky-note';
              const ActiveIcon =
                activeToolId === 'pen'
                  ? penToolMode === 'highlighter'
                    ? Highlighter
                    : penToolMode === 'eraser'
                      ? Eraser
                      : PencilLine
                  : StickyNote;
              const label = toolLabel(toolId, 'Notes and media');

              return (
                <DropdownMenu key={toolId} open={noteMenuOpen} onOpenChange={setNoteMenuOpen}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={railButtonClass(isActive)}
                          data-testid="tid.editor.tool.note-tools"
                          aria-label={label}
                          aria-pressed={isActive}
                        >
                          <span className="relative flex h-5 w-5 items-center justify-center">
                            <ActiveIcon
                              className={railIconClass(isActive)}
                              strokeWidth={1.9}
                              style={activeToolId === 'pen' ? { color: penColor } : undefined}
                            />
                            {activeToolId === 'sticky-note' && selectedStickyColor && (
                              <span
                                className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-sm border border-white shadow-sm"
                                style={{ backgroundColor: selectedStickyColor.color }}
                              />
                            )}
                            <ChevronUp
                              className={cn(
                                "absolute -bottom-1.5 -right-1.5 h-3 w-3 opacity-65",
                                "text-[#64748B] dark:text-slate-300"
                              )}
                              strokeWidth={2.2}
                            />
                          </span>
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent key={label}>{label}</TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent
                    align="center"
                    side="top"
                    sideOffset={12}
                    className={cn(
                      "w-[232px] rounded-[18px] border-[#DDE3EA]/90 bg-white/[0.96] p-2.5",
                      "shadow-[0_22px_52px_rgba(15,23,42,0.18),0_4px_14px_rgba(15,23,42,0.08)] backdrop-blur-xl",
                      "dark:border-white/10 dark:bg-[#101722]/[0.96]"
                    )}
                  >
                    <div className="grid grid-cols-3 gap-1.5 rounded-[14px] bg-[#F1F5F9] p-1 dark:bg-white/[0.07]">
                      {noteMenuItems.map((item) => {
                        const Icon = item.id === 'pen' && penToolMode === 'highlighter'
                          ? Highlighter
                          : item.id === 'pen' && penToolMode === 'eraser'
                            ? Eraser
                            : item.icon;
                        const selected = item.id === noteMenuMode;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            data-testid={item.id === 'image-picker' ? 'tid.editor.tool.image-picker' : `tid.editor.tool.${item.id}`}
                            className={cn(
                              "relative h-9 rounded-[11px] border flex items-center justify-center transition-[background-color,border-color,box-shadow,transform]",
                              selected
                                ? "border-white bg-white text-[#0F172A] shadow-[0_5px_14px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-white/[0.14] dark:text-white"
                                : "border-transparent text-[#64748B] hover:bg-white/70 hover:text-[#0F172A] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                            )}
                            aria-label={item.label}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              if (item.id === 'sticky-note') {
                                setNoteMenuMode('sticky-note');
                                void activateStickyNoteMode(stickyColorId, false);
                                return;
                              }
                              if (item.id === 'pen') {
                                setNoteMenuMode('pen');
                                void activatePenMode();
                                return;
                              }
                              void handleNoteMenuSelect(item.id);
                            }}
                          >
                            <Icon
                              className="h-[17px] w-[17px] stroke-[1.9]"
                              strokeWidth={1.9}
                              style={item.id === 'pen' ? { color: penColor } : undefined}
                            />
                            {item.id === 'sticky-note' && selectedStickyColor && (
                              <span
                                className="absolute bottom-1 right-1 h-2 w-2 rounded-[3px] border border-white shadow-sm"
                                style={{ backgroundColor: selectedStickyColor.color }}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <DropdownMenuSeparator className="my-2.5 bg-[#E2E8F0] dark:bg-white/10" />

                    {noteMenuMode === 'sticky-note' ? (
                      <div className="grid grid-cols-4 gap-2">
                        {STICKY_NOTE_COLORS.map((noteColor) => (
                          <button
                            key={noteColor.id}
                            type="button"
                            data-testid={`tid.editor.stickyColor.${noteColor.id}`}
                            className={cn(
                              "relative h-10 rounded-[11px] border transition-[box-shadow,transform,border-color] hover:-translate-y-0.5",
                              "shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_4px_10px_rgba(15,23,42,0.08)]",
                              stickyColorId === noteColor.id ? "ring-2 ring-[#3478DD]/35" : undefined
                            )}
                            style={{
                              backgroundColor: noteColor.color,
                              borderColor: stickyColorId === noteColor.id ? '#3478DD' : noteColor.border,
                            }}
                            aria-label={noteColor.label}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void activateStickyNoteMode(noteColor.id);
                              setNoteMenuOpen(false);
                            }}
                          >
                            <span className="absolute left-1.5 right-1.5 top-1.5 h-px rounded-full bg-white/55" />
                            {stickyColorId === noteColor.id && (
                              <span className="absolute bottom-1.5 left-1/2 block h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#1F2937]" />
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-1.5 rounded-[14px] bg-[#F1F5F9] p-1 dark:bg-white/[0.07]">
                          {PEN_TOOL_OPTIONS.map((option) => {
                            const Icon = option.icon;
                            return (
                              <button
                                key={option.id}
                                type="button"
                                data-testid={`tid.editor.penMode.${option.id}`}
                                className={cn(
                                  "h-9 rounded-[11px] border flex items-center justify-center transition-[background-color,border-color,box-shadow,transform]",
                                  penToolMode === option.id
                                    ? "border-white bg-white shadow-[0_5px_14px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-white/[0.14]"
                                    : "border-transparent text-[#64748B] hover:bg-white/70 hover:text-[#0F172A] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white"
                                )}
                                aria-label={option.label}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  void activatePenMode({
                                    tool: option.id,
                                    width: option.width,
                                    opacity: option.opacity,
                                  });
                                  setNoteMenuOpen(false);
                                }}
                              >
                                <Icon className="h-[17px] w-[17px] stroke-[1.9]" strokeWidth={1.9} />
                              </button>
                            );
                          })}
                        </div>

                        {penToolMode !== 'eraser' && (
                          <div className="flex justify-center gap-2 pt-1.5">
                            {PEN_COLORS.map((color) => (
                              <button
                                key={color}
                                type="button"
                                data-testid={`tid.editor.penColor.${color.replace('#', '')}`}
                                className={cn(
                                  "h-7 w-7 rounded-full border-2 transition-[box-shadow,transform,border-color] hover:-translate-y-0.5",
                                  penColor === color ? "border-[#3478DD] shadow-[0_0_0_3px_rgba(52,120,221,0.14)]" : "border-white/70 dark:border-white/20"
                                )}
                                style={{ backgroundColor: color }}
                                aria-label={color}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  void activatePenMode({ color });
                                  setNoteMenuOpen(false);
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            const resolvedToolId = toolId === 'connector' ? 'genogram-connector' : toolId;
            const tool = orderedTools.find(t => t.manifest.id === resolvedToolId);
            if (!tool) return null;

            const isActive = (activeToolId === tool.manifest.id);
            const customIcon = getToolIcon(toolId, isActive);
            const label = toolLabel(toolId, tool.manifest.name || toolId);

            // All tools render as flat 40x40 buttons. The floating /
            // bottom-aligned / vertical-offset hacks the previous design
            // needed (because the colourful illustration icons were ~80px
            // tall and stuck out of a 52px toolbar) are gone — flat icons
            // sit inside the chip like every other tool in the side rail.
            //
            // Test-id selection preserves the two patterns the e2e suite
            // already depends on: `tid.editor.tool.<id>` for the canvas
            // authoring tools and the legacy `tool-<id>` for text / frames /
            // comments, which still have no-fallback specs in tests/e2e/.
            const testId = LEGACY_TESTID_TOOLS.has(tool.manifest.id)
              ? `tool-${tool.manifest.id}`
              : `tid.editor.tool.${toolId}`;

            return (
              <Tooltip key={tool.manifest.id}>
                <TooltipTrigger asChild>
                  <button
                    className={railButtonClass(isActive)}
                    data-testid={testId}
                    aria-label={label}
                    aria-pressed={isActive}
                    onClick={() => handleToolExecute(tool)}
                  >
                    {customIcon || (
                      <span className="text-base font-medium">
                        {tool.manifest.name?.charAt(0) || '?'}
                      </span>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent key={label}>{label}</TooltipContent>
              </Tooltip>
            );
          })}

          {/* Registry-driven clusters (emergent canvas chrome plan §5).
              canvasCreate: domain creation verbs (e.g. Atlas add person /
              position / department). canvasMode: mode toggles (scenario
              toggle lands here once its semantics are decided). Both render
              nothing when no manifests pass the gates. */}
          <ChromeMountPoint
            mount="canvasCreate"
            toolRegistry={toolRegistry}
            onToolExecute={onToolExecute}
            className="gap-1"
            leading={railDivider}
          />
          <ChromeMountPoint
            mount="canvasMode"
            toolRegistry={toolRegistry}
            onToolExecute={onToolExecute}
            className="gap-1"
            leading={railDivider}
          />

          {/* Separator before undo/redo */}
          {railDivider}

          {/* Undo/Redo Buttons */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn(
                  railButtonClass(false),
                  "disabled:opacity-40 disabled:cursor-not-allowed"
                )}
                data-testid="tid.editor.undo"
                onClick={onUndo}
                disabled={!canUndo}
                aria-label={t('toolbar.undo')}
              >
                <Undo2 className={railIconClass(false)} strokeWidth={1.9} />
              </button>
            </TooltipTrigger>
            <TooltipContent key={t('toolbar.undo')}>{t('toolbar.undo')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className={cn(
                  railButtonClass(false),
                  "disabled:opacity-40 disabled:cursor-not-allowed"
                )}
                data-testid="tid.editor.redo"
                onClick={onRedo}
                disabled={!canRedo}
                aria-label={t('toolbar.redo')}
              >
                <Redo2 className={railIconClass(false)} strokeWidth={1.9} />
              </button>
            </TooltipTrigger>
            <TooltipContent key={t('toolbar.redo')}>{t('toolbar.redo')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>


      {/* Zoom Controls - anchored to the primary canvas bottom-right */}
      <div data-testid="tid.editor.zoomToolbar" className={cn(
        "absolute bottom-6 right-6 z-50 bg-background border rounded-[24px] shadow-lg p-1.5 flex items-center gap-1"
      )}>
        <TooltipProvider skipDelayDuration={0} disableHoverableContent>
          {/* Theme Toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <ThemeToggle size="sm" testId="tid.editor.themeToggle" />
              </div>
            </TooltipTrigger>
            <TooltipContent key={t('tools.toggleTheme')}>{t('tools.toggleTheme')}</TooltipContent>
          </Tooltip>

          {/* Edge-to-edge separator before zoom controls */}
          <div className="relative self-stretch mx-0">
            <div className="absolute inset-y-0 left-0 w-px bg-border -my-1.5" />
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="tid.editor.zoom.out" onClick={onZoomOut} aria-label={t('canvas.zoomOut')}>
                <Minus className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent key={t('canvas.zoomOut')}>{t('canvas.zoomOut')}</TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-1.5 min-w-[56px] justify-center text-xs"
                    data-testid="tid.editor.zoom.menu"
                    aria-label={t('zoom.options')}
                  >
                    <span>{Math.round(zoom * 100)}%</span>
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent key={t('zoom.options')}>{t('zoom.options')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="center" className="w-[200px]">
              {zoomPresets.map((preset) => (
                <DropdownMenuItem
                  key={preset.value}
                  data-testid={`tid.editor.zoom.preset.${Math.round(preset.value * 100)}`}
                  onClick={() => {
                    if (approxEqual(preset.value, 1)) {
                      onZoomReset();
                    } else {
                      onZoomTo(preset.value);
                    }
                  }}
                  className={cn(
                    'flex items-center justify-between',
                    approxEqual(zoom, preset.value) ? 'bg-accent text-accent-foreground' : undefined
                  )}
                >
                  {preset.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="tid.editor.zoom.fit" onClick={onZoomFit}>{t('zoom.toFit')}</DropdownMenuItem>
              <DropdownMenuItem data-testid="tid.editor.zoom.reset" onClick={onZoomReset}>{t('zoom.resetView')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" data-testid="tid.editor.zoom.in" onClick={onZoomIn} aria-label={t('canvas.zoomIn')}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent key={t('canvas.zoomIn')}>{t('canvas.zoomIn')}</TooltipContent>
          </Tooltip>

          {/* Edge-to-edge separator before help */}
          <div className="relative self-stretch mx-0">
            <div className="absolute inset-y-0 left-0 w-px bg-border -my-1.5" />
          </div>

          {/* Help Icon */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                data-testid="tid.editor.help"
                aria-label={t('tools.help')}
                onClick={() => {
                  openChat();
                }}
              >
                <CircleHelp className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent key={t('tools.help')}>{t('tools.help')}</TooltipContent>
          </Tooltip>

          <>
            <Separator orientation="vertical" className="h-5 mx-0.5" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  data-minimap-toggle="true"
                  data-testid="tid.editor.minimap"
                  className={cn("h-7 w-7", minimapOpen ? 'bg-muted' : undefined)}
                  onClick={() => setMinimapOpen((prev) => !prev)}
                  aria-pressed={minimapOpen}
                  aria-label={t('tools.minimap', { defaultValue: 'Minimap' })}
                >
                  <Map className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent key={t('tools.minimap', { defaultValue: 'Minimap' })}>
                {t('tools.minimap', { defaultValue: 'Minimap' })}
              </TooltipContent>
            </Tooltip>
          </>
        </TooltipProvider>
      </div>

      {minimapOpen && (
        <FramesPanel
          onClose={() => setMinimapOpen(false)}
          onPanTo={handleMinimapPan}
        />
      )}
    </>
  );
};
